import { useState } from "react";
import { Authenticated, AuthLoading, Unauthenticated, useQuery } from "convex/react";
import { api } from "peek-cloud/_generated/api";
import type { PeekAuthClient } from "./lib/auth-client";
import { LoginForm } from "./components/LoginForm";
import { Shell } from "./components/Shell";

export function App({ authClient }: { authClient: PeekAuthClient }) {
  return (
    <>
      <AuthLoading>
        <div className="loading">Loading…</div>
      </AuthLoading>
      <Unauthenticated>
        <LoginScreen authClient={authClient} />
      </Unauthenticated>
      <Authenticated>
        <AuthenticatedShell authClient={authClient} />
      </Authenticated>
    </>
  );
}

function LoginScreen({ authClient }: { authClient: PeekAuthClient }) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSignIn(email: string, password: string) {
    setPending(true);
    setError(null);
    const { error: signInError } = await authClient.signIn.email({ email, password });
    setPending(false);
    if (signInError) setError(signInError.message ?? "Sign in failed");
  }

  async function handleSignUp(email: string, password: string, name: string) {
    setPending(true);
    setError(null);
    const { error: signUpError } = await authClient.signUp.email({ email, password, name });
    setPending(false);
    if (signUpError) setError(signUpError.message ?? "Sign up failed");
  }

  return <LoginForm pending={pending} error={error} onSignIn={handleSignIn} onSignUp={handleSignUp} />;
}

function AuthenticatedShell({ authClient }: { authClient: PeekAuthClient }) {
  const session = authClient.useSession();
  // Only ever mounted inside <Authenticated>, so this never fires while
  // unauthenticated — apps/peek-cloud/tenants.ts#getCurrentTenantId throws
  // for unauthenticated callers, and we never give it the chance to.
  const tenantId = useQuery(api.tenants.getCurrentTenantId);

  function handleSignOut() {
    void authClient.signOut();
  }

  return (
    <Shell userEmail={session.data?.user.email ?? "…"} tenantId={tenantId} onSignOut={handleSignOut} />
  );
}
